import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CardModule } from '@openng/optimus-ui/card';
import { TableModule } from '@openng/optimus-ui/table';
import { TagModule } from '@openng/optimus-ui/tag';
import { ButtonModule } from '@openng/optimus-ui/button';
import { SelectModule } from '@openng/optimus-ui/select';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { SessionsService } from '../../services/sessions.service';
import { UiStateService } from '../../services/ui-state.service';
import { PeriodOption } from '../../models/dashboard.types';
import { DateRangeSelectorComponent, DateRange } from '../shared/date-range-selector.component';
import {
  FilterOption,
  SessionSummary,
  SessionType,
  UserFilterOption,
} from '../../models/session.types';
import { debounceTime, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorBannerComponent } from '../shared/error-banner.component';

@Component({
  selector: 'app-sessions',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    TableModule,
    TagModule,
    ButtonModule,
    SelectModule,
    InputTextModule,
    DateRangeSelectorComponent,
    ErrorBannerComponent,
  ],
  templateUrl: './sessions.component.html',
  styleUrl: './sessions.component.scss',
})
export class SessionsComponent {
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  protected sessionsService = inject(SessionsService);
  protected uiState = inject(UiStateService);

  protected selectedPeriod = this.uiState.selectedPeriod;
  protected startDate = this.uiState.startDate;
  protected endDate = this.uiState.endDate;

  protected selectedOrg = signal<FilterOption | null>(null);
  protected selectedUser = signal<UserFilterOption | null>(null);
  protected textSearch = signal('');
  protected firstRow = signal(0);
  private searchSubject = new Subject<void>();
  private userFilterSubject = new Subject<string>();

  protected orgOptions = computed(() => this.sessionsService.filters().organisations);

  protected userOptions = computed(() => {
    const users = this.sessionsService.filters().users;
    const orgId = this.uiState.sessionOrgId();
    if (orgId == null) return users;
    return users.filter((u) => u.organisationId === orgId);
  });

  protected userFilterPlaceholder = computed(() =>
    this.sessionsService.filters().usersTruncated
      ? 'Type to search all users...'
      : 'Search users...',
  );

  constructor() {
    this.route.queryParamMap.subscribe((params) => {
      const orgRaw = params.get('org');
      const userRaw = params.get('user');
      const q = params.get('q');

      if (orgRaw) this.uiState.sessionOrgId.set(Number(orgRaw));
      if (userRaw) this.uiState.sessionUserId.set(Number(userRaw));
      if (q) this.uiState.sessionSearch.set(q);

      this.applySavedFilters();
    });

    effect(() => {
      this.sessionsService.loadFilters();
    });

    effect(() => {
      this.uiState.startDate();
      this.uiState.endDate();
      untracked(() => this.reloadFromPageOne());
    });

    effect(() => {
      const orgId = this.uiState.sessionOrgId();
      const userId = this.uiState.sessionUserId();
      this.matchFiltersFromState(orgId, userId);
      this.textSearch.set(this.uiState.sessionSearch());
    });

    this.searchSubject
      .pipe(debounceTime(400), takeUntilDestroyed())
      .subscribe(() => this.reloadFromPageOne());

    this.userFilterSubject
      .pipe(debounceTime(400), takeUntilDestroyed())
      .subscribe((term) => this.sessionsService.loadFilters(term));
  }

  loadData(event?: { first?: number | null; rows?: number | null } | undefined) {
    const { startDate, endDate } = this.uiState.getRange();
    const first = event?.first ?? 0;
    const rows = event?.rows ?? 20;
    const page = event ? (first || 0) / rows : 0;
    this.sessionsService.loadSessions(
      startDate,
      endDate,
      this.uiState.sessionOrgId() ?? undefined,
      this.uiState.sessionUserId() ?? undefined,
      this.textSearch(),
      page,
      rows,
    );
  }

  private reloadFromPageOne() {
    this.firstRow.set(0);
    this.loadData();
  }

  onPeriodChange(period: PeriodOption) {
    this.uiState.setPeriod(period);
  }

  onDateRangeChange(dateRange: DateRange) {
    this.uiState.setRange(dateRange.startDate, dateRange.endDate);
  }

  onFilterChange() {
    const org = this.selectedOrg();
    const user = this.selectedUser();
    if (org && user && user.organisationId !== org.id) {
      this.selectedUser.set(null);
    }
    this.uiState.sessionOrgId.set(org?.id ?? null);
    this.uiState.sessionUserId.set(this.selectedUser()?.id ?? null);
    this.reloadFromPageOne();
  }

  onUserFilterChange() {
    this.uiState.sessionUserId.set(this.selectedUser()?.id ?? null);
    this.reloadFromPageOne();
  }

  onSearchInput() {
    this.uiState.sessionSearch.set(this.textSearch());
    this.searchSubject.next();
  }

  onUserFilter(event: { filter?: unknown }) {
    this.userFilterSubject.next(typeof event.filter === 'string' ? event.filter : '');
  }

  clearFilters() {
    this.uiState.clearSessionFilters();
    this.selectedOrg.set(null);
    this.selectedUser.set(null);
    this.textSearch.set('');
    this.reloadFromPageOne();
  }

  openDetail(session: SessionSummary): void {
    this.router.navigate(['/sessions', this.getRouteType(session.type), session.id]);
  }

  getStatusSeverity(
    status: string,
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'ACTIVE':
        return 'info';
      case 'ARCHIVED':
        return 'secondary';
      default:
        return 'contrast';
    }
  }

  getUserDisplayName(user: UserFilterOption): string {
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return name ? `${name} (${user.email})` : user.email;
  }

  openOrg(session: SessionSummary) {
    if (session.organisationId != null) {
      this.router.navigate(['/organizations', session.organisationId]);
    }
  }

  private applySavedFilters() {
    const orgId = this.uiState.sessionOrgId();
    const userId = this.uiState.sessionUserId();
    this.matchFiltersFromState(orgId, userId);
    this.textSearch.set(this.uiState.sessionSearch());
  }

  private matchFiltersFromState(orgId: number | null, userId: number | null) {
    const orgs = this.sessionsService.filters().organisations;
    if (orgId != null) {
      this.selectedOrg.set(orgs.find((o) => o.id === orgId) ?? null);
    }
    const users = this.sessionsService.filters().users;
    if (userId != null) {
      this.selectedUser.set(users.find((u) => u.id === userId) ?? null);
    }
  }

  private getRouteType(type: SessionType): string {
    return type.toLowerCase();
  }
}
